import { type Modifier, strings } from "@quickpoint.me/shared";
import { useContext, useLayoutEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import type { StyledBackgroundGridCellProps } from "../../@types/viewerati";
import useFx from "../../lib/animations/fxHook";
import createImageUrl from "../../lib/createImageUrl";
import { opacityHelper } from "../../lib/cssHelpers";
import { setMetaTagColor } from "../../lib/setColorMetaTag";
import { FilterColors } from "../../state/filter";
import {
	IsInPlayWrightTest,
	PageSizeAtom,
	StickyHeaderElementsToDisplay,
} from "../../state/page";
import { ImageMaps } from "../../state/selectors";
import { IsInAuthor, IsInPopOut, TapDirection } from "../../state/ui";
import type { PositionContextType } from "../displayElements/DisplayElements";
import { SplitPageContext } from "../displayElements/RenderSplitPage";

const StyledCell = styled.div<StyledBackgroundGridCellProps>`
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  display: flex;
  --cell-opacity: ${(props) => (props.$opacity ? props.$opacity : 1)};
  --cell-blur: ${(props) => (props.$blur ? props.$blur : 0)};
  background: ${(props) => (props.$isInGridCell ? "unset" : props.$background)};
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
  filter: blur(var(--cell-blur)) opacity(var(--cell-opacity));
  ${(props) => {
		if (props.$hideOn === "desktop" && props.$pageSize.landscape)
			return "display: none;";
		if (props.$hideOn === "mobile" && !props.$pageSize.landscape)
			return "display: none;";
		return "";
	}};
`;

const BackgroundGridCell = ({
	atomId,
	content,
	pageIndex,
	position,
	tapped,
	modifier,
	isInGridCell,
}: {
	atomId: number;
	content: string;
	pageIndex: number;
	position: PositionContextType;
	tapped: boolean;
	modifier: Modifier | undefined;
	isInGridCell: boolean;
}) => {
	content = content.trim();
	const pageSize = useRecoilValue(PageSizeAtom);
	const isInAuthor = useRecoilValue(IsInAuthor);

	const backGroundRef = useRef<HTMLDivElement>(null);
	const baseFontSize = pageSize.baseFontSize;
	const direction = useRecoilValue(TapDirection);
	const filterColors = useRecoilValue(FilterColors);
	const fxClass = useFx({ pageIndex, atomId, isInAuthor });
	const imageMaps = useRecoilValue(ImageMaps);
	const inPopOut = useRecoilValue(IsInPopOut);
	const isInPlayWrightTest = useRecoilValue(IsInPlayWrightTest);
	const isInSplitPage = useContext(SplitPageContext);
	const stickyHeaderElementsToDisplay = useRecoilValue(
		StickyHeaderElementsToDisplay,
	);

	const image = imageMaps.find((map) => map.userDefinedName === content);

	let background = "unset";

	// is it a color rather than an uploaded image?
	if (!image && typeof content === "string") {
		// @ts-expect-error: GQL types
		if (filterColors[content]) {
			// @ts-expect-error: GQL types
			background = filterColors[content];
		} else {
			background = content;
		}
	}

	if (image) {
		background = `url(${createImageUrl(image)})`;
	}

	const opacity = opacityHelper(tapped, modifier?.["opacity:"]).toString();
	const blur = modifier?.["blur:"] ? `${modifier["blur:"]}px` : "0";

	useLayoutEffect(() => {
		if (backGroundRef.current && isInGridCell) {
			// if it's in a grid the background is applied to the containing cell
			const containingGridCell = backGroundRef.current?.closest(
				strings.css.styledGridCellClass,
			) as HTMLDivElement;
			if (containingGridCell) {
				containingGridCell.style.setProperty("--cell-background", background);
				if (modifier?.["opacity:"]) {
					containingGridCell.style.setProperty("--cell-opacity", opacity);
				}
				if (modifier?.["blur:"]) {
					containingGridCell.style.setProperty("--cell-blur", blur);
				}
			}
		}
	});

	// set the meta color for iOs
	useLayoutEffect(() => {
		if (isInSplitPage || stickyHeaderElementsToDisplay.length > 0) return;
		if (image) {
			setMetaTagColor("#000");
			return;
		}
		if (direction === "init" && position === "left") {
			// timeout busts iOS cache onload
			setTimeout(() => setMetaTagColor(background), 10);
		}

		if (direction === "back" && position === "left") {
			setMetaTagColor(background);
		}

		if (direction === "forward" && position === "right") {
			setMetaTagColor(background);
		}
	}, [
		background,
		isInSplitPage,
		stickyHeaderElementsToDisplay.length,
		position,
		direction,
		image,
	]);
	return (
		<StyledCell
			$background={background}
			$elementIndex={0}
			$filterColors={filterColors}
			$isInFlexBox={true}
			$isInGridCell={isInGridCell}
			$isInPLaywrightTest={isInPlayWrightTest}
			$isInPopOut={inPopOut}
			$blur={blur}
			$opacity={opacity}
			className={fxClass}
			// @ts-expect-error
			$hideOn={modifier?.["hideOn:"]}
			$pageSize={pageSize}
			$isInPortrait={!pageSize.landscape}
			$tapped={tapped}
			$modifier={modifier}
			data-testid="background-grid-cell"
			ref={backGroundRef}
			$baseFontSize={baseFontSize}
		/>
	);
};

export default BackgroundGridCell;
